/**
 * Shared formatting/normalisation helpers for API payloads.
 *
 * The backend returns some fields in more than one shape depending on the code
 * path (notably `persona_match.cohort`, which is a plain string on some records
 * and a `{label, avg_score, ...}` object on others). Rendering the object form
 * directly throws "Objects are not valid as a React child" and takes the whole
 * page down to the ErrorBoundary, so every read goes through here.
 */

/** Human label for a persona cohort, whatever shape the backend sent. */
export function cohortLabel(persona, fallback = "General") {
    const cohort = persona?.cohort;
    if (!cohort) return fallback;
    if (typeof cohort === "string") return cohort || fallback;
    if (typeof cohort === "object") return cohort.label || cohort.name || fallback;
    return String(cohort);
}

/**
 * Cohort average score, or null when the backend did not supply a real number.
 *
 * Previously written as `a || b ? 680 : national`, which parses as
 * `(a || b) ? 680 : national` and therefore reported a hardcoded 680 for every
 * user who matched any cohort at all.
 */
export function computeCohortAvg(persona) {
    const avg = Number(persona?.cohort?.avg_score);
    return Number.isFinite(avg) && avg > 0 ? avg : null;
}

/**
 * Turn a FastAPI error payload into one human sentence.
 * `detail` may be a string, a list of pydantic validation objects, a single
 * object, or absent entirely (network/CORS failure).
 */
export function formatError(detail, fallback = "Something went wrong. Please try again.") {
    if (detail == null) return fallback;
    if (typeof detail === "string") return detail || fallback;
    if (Array.isArray(detail)) {
        const parts = detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : typeof e === "string" ? e : null))
            .filter(Boolean);
        return parts.length ? parts.join(" ") : fallback;
    }
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    return fallback;
}

/** Same as formatError but takes the axios error itself. */
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
    if (!err?.response) {
        if (err?.code === "ECONNABORTED") return "That took too long. Please try again.";
        return "Can't reach the server. Check your connection and try again.";
    }
    return formatError(err.response?.data?.detail, fallback);
}

/** Compact Indian number form for tight mobile layouts: 1500000 -> "15L+". */
export function compactIndian(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value ?? "");
    if (n >= 10000000) return `${+(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)}Cr`;
    if (n >= 100000) return `${+(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
    if (n >= 1000) return `${+(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return String(n);
}

/** Rupee amount with Indian digit grouping, no decimals. */
export function formatINR(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
